import React from 'react';

import { bool, func, number, shape, string } from 'prop-types';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { CheckCircle } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  cardRoot: {
    width: '100%',
    border: `2px solid ${theme.palette.neutral.light}`,
    borderRadius: 4,
    boxShadow: 'none',
    marginBottom: theme.spacing(2),
    margin: '0 auto',
    maxWidth: 540,
    [theme.breakpoints.up('md')]: {
      maxWidth: 400,
      marginBottom: 0,
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  cardSelected: {
    border: `2px solid ${theme.palette.secondary.main}`,
    boxShadow: '0px 5px 15px rgba(116, 124, 130, 0.3)',
  },
  cardMediaImg: {
    objectFit: 'contain',
  },
  titleRoot: {
    maxWidth: '100%',
    ...theme.typography.body1,
    fontSize: ({ isTitleOnly }) => (isTitleOnly ? '1.25rem' : theme.typography.body1.fontSize),
    fontFamily: theme.typography.h1.fontFamily,
    [theme.breakpoints.up('md')]: {
      maxWidth: ({ isTitleOnly }) => (isTitleOnly ? 150 : '100%'),
    },
  },
  checkmarkContainer: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  cardActionAreaRoot: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: ({ isTitleOnly }) => (isTitleOnly ? 'center' : 'space-between'),
  },
  cardContentRoot: {
    padding: theme.spacing(2, 4, 4, 4),
    height: 150,
  },
  cardContentRootTitleOnly: {
    padding: theme.spacing(2),
  },
}));

const SelectableCard = ({ title, description, icon, isSelected, onClick, id }) => {
  const isTitleOnly = !(icon?.svg || description);
  const classes = useStyles({ isTitleOnly });

  const media = icon?.svg && (
    <CardMedia
      component="img"
      alt={icon.alt}
      height={icon.height}
      src={icon.svg}
      classes={{
        img: classes.cardMediaImg,
      }}
      id={`${id}-icon`}
    />
  );

  const descriptionText = description && (
    <Typography variant="body2" color="textSecondary" id={`${id}-description`}>
      {description}
    </Typography>
  );

  const checkmark = isSelected && (
    <Box className={classes.checkmarkContainer} id={`${id}-selected`}>
      <CheckCircle color="secondary" fontSize="large" />
    </Box>
  );

  return (
    <Card
      classes={{
        root: classes.cardRoot,
      }}
      className={isSelected ? classes.cardSelected : null}
    >
      <CardActionArea
        classes={{
          root: classes.cardActionAreaRoot,
        }}
        onClick={onClick}
        id={id}
      >
        {checkmark}
        {media}
        <CardContent
          classes={{
            root: isTitleOnly ? classes.cardContentRootTitleOnly : classes.cardContentRoot,
          }}
        >
          <Typography
            classes={{
              root: classes.titleRoot,
            }}
            align={!(icon && description) ? 'center' : 'left'}
            id={`${id}-title`}
          >
            {title}
          </Typography>
          {descriptionText}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

SelectableCard.propTypes = {
  title: string.isRequired,
  description: string,
  icon: shape({
    alt: string,
    svg: string,
    height: number,
  }),
  id: string.isRequired,
  isSelected: bool.isRequired,
  onClick: func.isRequired,
};

SelectableCard.defaultProps = {
  description: '',
  icon: {},
};

export default SelectableCard;
